import { IS_FACTURACION } from '../config';

const CANASTA_KEY = IS_FACTURACION ? 'oh_factura_canasta_drafts_v1' : 'oh_bodega_canasta_drafts_v1';
const QUEUE_KEY = IS_FACTURACION ? 'oh_factura_offline_queue_v1' : 'oh_bodega_offline_queue_v1';

function safeParse(raw, fallback) {
  try {
    const data = JSON.parse(raw);
    return data ?? fallback;
  } catch {
    return fallback;
  }
}

export function isNetworkError(err) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const msg = String(err?.message || err || '');
  return /No se pudo conectar|Failed to fetch|NetworkError|ERR_INTERNET|ERR_NETWORK|conexión|offline|Load failed|network|tardó demasiado|AbortError/i.test(
    msg
  );
}

export function loadCanastaDraft(pedidoId) {
  if (!pedidoId) return null;
  try {
    const all = safeParse(localStorage.getItem(CANASTA_KEY) || '{}', {});
    const row = all[String(pedidoId)];
    if (!row || (!Array.isArray(row.keys) && !Array.isArray(row.missing_keys))) return null;
    return row;
  } catch {
    return null;
  }
}

export function saveCanastaDraft(pedidoId, keys, revision = null, missingKeys = []) {
  if (!pedidoId) return;
  try {
    const all = safeParse(localStorage.getItem(CANASTA_KEY) || '{}', {});
    all[String(pedidoId)] = {
      keys: [...keys].map(String),
      missing_keys: [...(missingKeys || [])].map(String),
      revision: revision != null ? Number(revision) : null,
      updated_at: new Date().toISOString(),
    };
    localStorage.setItem(CANASTA_KEY, JSON.stringify(all));
  } catch {
    /* ignore quota */
  }
}

export function clearCanastaDraft(pedidoId) {
  if (!pedidoId) return;
  try {
    const all = safeParse(localStorage.getItem(CANASTA_KEY) || '{}', {});
    delete all[String(pedidoId)];
    localStorage.setItem(CANASTA_KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

function loadQueue() {
  try {
    const rows = safeParse(localStorage.getItem(QUEUE_KEY) || '[]', []);
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

function saveQueue(rows) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(rows));
}

export function offlineBodegaQueueCount() {
  return loadQueue().length;
}

/**
 * Encola canasta o notas para enviar al volver internet.
 * Sustituye mutación pendiente del mismo tipo+pedido.
 */
export function enqueueBodegaMutation(entry) {
  const rows = loadQueue().filter(
    (r) => !(r.type === entry.type && String(r.pedidoId) === String(entry.pedidoId))
  );
  rows.push({
    ...entry,
    id: `${entry.type}-${entry.pedidoId}-${Date.now()}`,
    created_at: new Date().toISOString(),
    attempts: 0,
  });
  saveQueue(rows);
  return rows.length;
}

export async function flushBodegaOfflineQueue({ saveCanasta, saveNotas }) {
  const queue = loadQueue();
  const sent = [];
  for (const entry of queue) {
    try {
      if (entry.type === 'canasta') {
        await saveCanasta(
          entry.pedidoId,
          entry.keys,
          entry.separadorNombre,
          entry.revision,
          entry.missing_keys || []
        );
        clearCanastaDraft(entry.pedidoId);
      } else if (entry.type === 'notas') {
        await saveNotas(entry.pedidoId, entry.notas, entry.separadorNombre);
      } else {
        saveQueue(loadQueue().filter((r) => r.id !== entry.id));
        continue;
      }
      saveQueue(loadQueue().filter((r) => r.id !== entry.id));
      sent.push(entry);
    } catch (err) {
      if (err?.status === 409) {
        clearCanastaDraft(entry.pedidoId);
        saveQueue(loadQueue().filter((r) => r.id !== entry.id));
        continue;
      }
      const rows = loadQueue().map((r) =>
        r.id === entry.id
          ? { ...r, attempts: (r.attempts || 0) + 1, last_error: err.message || 'Error' }
          : r
      );
      saveQueue(rows);
      if (isNetworkError(err)) {
        return { sent, remaining: offlineBodegaQueueCount(), stopped: true };
      }
    }
  }
  return { sent, remaining: offlineBodegaQueueCount(), stopped: false };
}
